import { useState } from 'react';

import { ApiCallError, apiFetch } from '../lib/api.ts';
import { useUser } from '../hooks/useUser.ts';

/**
 * Self-service password change for the logged-in user. Posts to the existing
 * `POST /api/auth/change-password` endpoint, which verifies the current
 * password server-side before updating. Available to ANY authenticated user.
 */
export function ChangePasswordPage() {
  const { user } = useUser();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setDone(false);

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirm) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from the current one.');
      return;
    }

    setSubmitting(true);
    try {
      await apiFetch('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      setDone(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
    } catch (err) {
      if (err instanceof ApiCallError && err.status === 401) {
        setError('Current password is incorrect.');
      } else {
        setError((err as Error).message || 'Could not change password.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Change password</h1>
        <span className="main-subtitle">{user ? user.email : ''}</span>
      </header>

      <form onSubmit={onSubmit} style={{ maxWidth: 420, display: 'grid', gap: 12 }}>
        <label style={{ display: 'grid', gap: 4 }}>
          <span>Current password</span>
          <input
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span>New password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            At least 8 characters.
          </span>
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span>Confirm new password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </label>

        {error && <div className="error">{error}</div>}
        {done && (
          <div className="badge-ok" style={{ padding: '8px 12px' }}>
            Password changed successfully.
          </div>
        )}

        <button type="submit" className="btn" disabled={submitting}>
          {submitting ? 'Saving…' : 'Change password'}
        </button>
      </form>
    </>
  );
}
